import { AccountSelector, Alert, Button, ModalV2 } from '@components'
import { AccountType, VatPostingMethod } from '@constants'
import { FeatureWrapper, useBusinessContext } from '@context'
import { fetchBusiness, IBusiness, updateBusiness } from '@query'
import { applyHttpFormErrors } from '@utils'
import { Controller, get, useForm } from 'react-hook-form'
import React, { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaBook, FaSave } from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { SettingsAccountField } from '@pages/SettingsPage/components/SettingsAccountField'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'

export const DefaultAccountSettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [isModalVisible, setIsModalVisible] = useState(false)

  const getDefaultValues = useCallback((data?: Partial<IBusiness>): Partial<IBusiness> => {
    const {
      account_yle_tax: account_yle_tax_id,
      account_tax_deferrals: account_tax_deferrals_id,
      account_income_tax_rec: account_income_tax_rec_id,
      account_income_tax_lia: account_income_tax_lia_id,
      account_previous_profit: account_previous_profit_id,
      account_vat_liabilities: account_vat_liabilities_id,
      account_vat_accrual_adjustment: account_vat_accrual_adjustment_id,
      account_vat_receivables: account_vat_receivables_id,
      account_trade_receivables: account_trade_receivables_id,
      account_payables: account_payables_id,
      account_cash_payment: account_cash_payment_id
    } = data || {}

    return {
      account_yle_tax: account_yle_tax_id,
      account_tax_deferrals: account_tax_deferrals_id,
      account_income_tax_rec: account_income_tax_rec_id,
      account_income_tax_lia: account_income_tax_lia_id,
      account_previous_profit: account_previous_profit_id,
      account_vat_liabilities: account_vat_liabilities_id,
      account_vat_accrual_adjustment: account_vat_accrual_adjustment_id,
      account_vat_receivables: account_vat_receivables_id,
      account_trade_receivables: account_trade_receivables_id,
      account_payables: account_payables_id,
      account_cash_payment: account_cash_payment_id
    }
  }, [])

  const { data: businessData, refetch } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    refetchOnMount: false
  })

  const form = useForm<Partial<IBusiness>>({
    defaultValues: async () => {
      const { data } = await refetch()
      return getDefaultValues(data)
    }
  })

  const {
    handleSubmit,
    setError,
    reset,
    control,
    formState: { isDirty, isSubmitting, errors }
  } = form

  const updateMutation = useMutation<IBusiness, unknown, Partial<IBusiness>>({
    mutationFn: data => updateBusiness({ slug }, data),
    ...{
      onSuccess: async data => {
        reset(getDefaultValues(data))
        await queryClient.invalidateQueries({ queryKey: [slug] })
        await queryClient.invalidateQueries({ queryKey: ['businesses'] })
        toast.success(t('general.changesSaved'))
        setIsModalVisible(false)
      },
      onError: error => applyHttpFormErrors(error, setError)
    }
  })

  const normalizeEnum = useCallback((value?: string | null) => (value ?? '').toLowerCase(), [])

  const isVatPostingMethodNet =
    normalizeEnum(businessData?.vat_posting_method) === normalizeEnum(VatPostingMethod.NET)
  const isVatPostingMethodSplit =
    normalizeEnum(businessData?.vat_posting_method) === normalizeEnum(VatPostingMethod.SPLIT)

  const onSubmit = handleSubmit(data => updateMutation.mutateAsync(data))

  return (
    <SettingsSubPageLayout title={t('settings.sections.defaultAccounts')}>
      <SettingsDataCard
        icon={<FaBook />}
        title={t('settings.sections.defaultAccounts')}
        action={
          <Button
            isSecondary
            size="sm"
            onClick={() => {
              reset(getDefaultValues(businessData))
              setIsModalVisible(true)
            }}
          >
            {t('general.edit')}
          </Button>
        }
        banner={
          <Alert
            title={t('settings.accountsPage.info.title')}
            description={
              <ReactMarkdown>{t('settings.accountsPage.info.description')}</ReactMarkdown>
            }
            marginBottom={0}
          />
        }
      >
        {normalizeEnum(businessData?.vat_posting_method) ===
          normalizeEnum(VatPostingMethod.NET) && (
          <SettingsAccountField
            label={t('settings.accountsPage.account.vatNet.name')}
            accountId={businessData?.account_vat_liabilities}
          />
        )}

        {normalizeEnum(businessData?.vat_posting_method) ===
          normalizeEnum(VatPostingMethod.SPLIT) && (
          <>
            <SettingsAccountField
              label={t('settings.accountsPage.account.vatLiabilities.name')}
              accountId={businessData?.account_vat_liabilities}
            />
            <SettingsAccountField
              label={t('settings.accountsPage.account.vatReceivables.name')}
              accountId={businessData?.account_vat_receivables}
            />
          </>
        )}

        <FeatureWrapper feature="vat_not_due_release">
          <SettingsAccountField
            label={t('settings.accountsPage.account.vatNotDue.name')}
            accountId={businessData?.account_vat_accrual_adjustment}
          />
        </FeatureWrapper>

        <SettingsAccountField
          label={t('settings.accountsPage.account.previousProfit.name')}
          accountId={businessData?.account_previous_profit}
        />

        <SettingsAccountField
          label={t('settings.accountsPage.account.tradeReceivables.name')}
          accountId={businessData?.account_trade_receivables}
        />
        <SettingsAccountField
          label={t('settings.accountsPage.account.accountPayables.name')}
          accountId={businessData?.account_payables}
        />
        <SettingsAccountField
          label={t('settings.accountsPage.account.paymentAccount.name')}
          accountId={businessData?.account_cash_payment}
        />
      </SettingsDataCard>

      <ModalV2
        header={t('settings.sections.defaultAccounts')}
        isVisible={isModalVisible}
        handleOnClose={() => setIsModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              form="default-account-settings-form"
              intent="success"
              icon={<FaSave />}
              disabled={!isDirty || updateMutation.isPending}
              showSpinner={isSubmitting || updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="default-account-settings-form" onSubmit={onSubmit}>
          <ModalSection>
            {isVatPostingMethodNet && (
              <Controller
                control={control}
                name="account_vat_liabilities"
                render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                  <AccountSelector
                    inputRef={ref}
                    label={t('settings.accountsPage.account.vatNet.name')}
                    value={value}
                    onChange={onChange}
                    errors={error}
                    info={t('settings.accountsPage.account.vatNet.info')}
                    accountFilter={{ type: [AccountType.LIA_VAT] }}
                  />
                )}
              />
            )}

            {isVatPostingMethodSplit && (
              <>
                <Controller
                  control={control}
                  name="account_vat_liabilities"
                  render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                    <AccountSelector
                      inputRef={ref}
                      label={t('settings.accountsPage.account.vatLiabilities.name')}
                      value={value}
                      onChange={onChange}
                      errors={error}
                      info={t('settings.accountsPage.account.vatLiabilities.info')}
                      accountFilter={{ type: AccountType.LIA_VAT }}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="account_vat_receivables"
                  render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                    <AccountSelector
                      inputRef={ref}
                      label={t('settings.accountsPage.account.vatReceivables.name')}
                      value={value}
                      onChange={onChange}
                      errors={error}
                      info={t('settings.accountsPage.account.vatReceivables.info')}
                      accountFilter={{ type: AccountType.ASS_VAT }}
                    />
                  )}
                />
              </>
            )}

            <FeatureWrapper feature="vat_not_due_release">
              <Controller
                control={control}
                name="account_vat_accrual_adjustment"
                render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                  <AccountSelector
                    inputRef={ref}
                    label={t('settings.accountsPage.account.vatNotDue.name')}
                    value={value}
                    onChange={onChange}
                    errors={error}
                    info={t('settings.accountsPage.account.vatNotDue.info')}
                  />
                )}
              />
            </FeatureWrapper>
          </ModalSection>

          <ModalSection>
            <Controller
              control={control}
              name="account_previous_profit"
              render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                <AccountSelector
                  inputRef={ref}
                  label={t('settings.accountsPage.account.previousProfit.name')}
                  value={value}
                  onChange={onChange}
                  errors={error}
                  info={t('settings.accountsPage.account.previousProfit.info')}
                  accountFilter={{ type: AccountType.LIA_PRE }}
                />
              )}
            />
          </ModalSection>

          <ModalSection>
            <Controller
              control={control}
              name="account_trade_receivables"
              render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                <AccountSelector
                  ref={ref}
                  id={'settings-account-receivables'}
                  label={t('settings.accountsPage.account.tradeReceivables.name')}
                  info={t('settings.accountsPage.account.tradeReceivables.info')}
                  error={error}
                  value={value}
                  onChange={onChange}
                  errors={get(errors, 'account_trade_receivables')}
                  queryKey="account-receivables"
                  accountFilter={{ type: AccountType.ASS_DUE }}
                />
              )}
            />

            <Controller
              control={control}
              name="account_payables"
              render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                <AccountSelector
                  id={'settings-account-payables'}
                  label={t('settings.accountsPage.account.accountPayables.name')}
                  info={t('settings.accountsPage.account.accountPayables.info')}
                  ref={ref}
                  error={error}
                  value={value}
                  onChange={onChange}
                  errors={get(errors, 'account_payables')}
                  queryKey="account-payables"
                  accountFilter={{ type: AccountType.LIA_DUE }}
                />
              )}
            />
            <Controller
              control={control}
              name="account_cash_payment"
              render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                <AccountSelector
                  id={'settings-account-cash-payment'}
                  label={t('settings.accountsPage.account.paymentAccount.name')}
                  info={t('settings.accountsPage.account.paymentAccount.info')}
                  ref={ref}
                  error={error}
                  value={value}
                  onChange={onChange}
                  errors={get(errors, 'account_cash_payment')}
                  queryKey="account-cash-payment"
                  accountFilter={{ type: AccountType.ASS_PAY }}
                />
              )}
            />
          </ModalSection>
        </form>
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  justify-content: space-between;
  width: 100%;
`
